<template>
  <b-modal
      id="alerts-folio-modal"
      hide-header-close
      hide-footer
      modal-class="alerts-folio-modal"
      dialog-class="alerts-folio-modal__dialog"
      content-class="alerts-folio-modal__content"
      header-class="alerts-folio-modal__header"
      body-class="alerts-folio-modal__body"
  >
    <template #modal-title>
      <div class="folio-alert-title">
        <span class="title-label">{{ headerLabel }}</span>
        <span class="title-meta">{{ headerMeta }}</span>
      </div>
    </template>

    <div class="modal-body-content">
      <div class="message-block">
        <p v-for="(line, idx) in bodyLines" :key="idx" class="message-line">
          {{ line }}
        </p>
      </div>

      <template v-if="hasChartDetails">
        <div class="charts-block">
          <div class="charts-label">
            {{ chartsLabel }}
          </div>
          <ul class="charts-list">
            <li v-for="chart in chartPreview" :key="`${chart.type || 'chart'}-${chart.id}`" class="charts-item">
              <span class="chart-id">{{ chart.id }}</span>
              <span class="chart-days" :class="getDaysClass(chart.daysLeft)">
                {{ formatDaysLeft(chart.daysLeft) }}
              </span>
            </li>
          </ul>
          <div v-if="extraChartsCount > 0" class="charts-footnote">
            +{{ extraChartsCount }} more chart(s)
          </div>
        </div>
      </template>
    </div>

    <div class="modal-actions">
      <template v-if="showPrimary">
        <b-button
            variant="outline-secondary"
            size="sm"
            class="action-button"
            @click="handleClose"
        >
          Close
        </b-button>
        <b-button
            variant="primary"
            size="sm"
            class="action-button primary"
            @click="handlePrimary"
        >
          {{ primaryLabel }}
        </b-button>
      </template>
      <b-button
          v-else
          variant="primary"
          size="sm"
          class="action-button primary"
          @click="handleClose"
      >
        OK
      </b-button>
    </div>
  </b-modal>
</template>

<script>
export default {
  props: {
    payload: {
      type: Object,
      default: null,
    },
  },
  computed: {
    status() {
      return this.payload?.status || 'unknown'
    },
    stats() {
      const defaults = { total: 0, expired: 0, expiringSoon: 0, healthy: 0 }
      if (!this.payload?.stats || typeof this.payload.stats !== 'object') {
        return defaults
      }
      return { ...defaults, ...this.payload.stats }
    },
    headerLabel() {
      switch (this.status) {
        case 'expired_all':
          return 'Folio coverage expired'
        case 'expiring':
          return 'Charts expiring soon'
        case 'healthy':
          return 'Charts up to date'
        case 'no_data':
          return 'No chart data available'
        case 'error':
          return 'Folio status unavailable'
        default:
          return 'Checking folio status'
      }
    },
    headerMeta() {
      const shipLabel = this.payload?.shipName || 'Folio overview'
      const updatedAt = this.payload?.updatedAt
      if (updatedAt) {
        const date = new Date(updatedAt)
        if (!Number.isNaN(date.getTime())) {
          return `${shipLabel} · Updated ${date.toLocaleString()}`
        }
      }
      return shipLabel
    },
    bodyLines() {
      const lines = []
      switch (this.status) {
        case 'expired_all':
          lines.push('All purchased charts for this vessel have expired.')
          if (this.stats.total > 0) {
            lines.push(`Expired: ${this.stats.expired} of ${this.stats.total}.`)
          }
          lines.push('Open the routes section to renew coverage for the planned voyage.')
          break
        case 'expiring':
          lines.push('Some charts will expire within the next 30 days.')
          if (this.stats.expiringSoon > 0) {
            lines.push(`Expiring soon: ${this.stats.expiringSoon}.`)
          }
          if (this.stats.expired > 0) {
            lines.push(`Already expired: ${this.stats.expired}.`)
          }
          lines.push('Review the folio and order replacements to stay compliant.')
          break
        case 'healthy':
          lines.push('The folio contains valid coverage for the vessel.')
          if (this.stats.healthy > 0) {
            lines.push(`Valid charts: ${this.stats.healthy}.`)
          }
          if (this.stats.expiringSoon > 0) {
            lines.push(`Expiring soon: ${this.stats.expiringSoon}.`)
          }
          if (this.stats.expired > 0) {
            lines.push(`Expired charts: ${this.stats.expired}.`)
          }
          lines.push('No immediate action is required, but keep monitoring expiring charts.')
          break
        case 'no_data':
          lines.push('We could not find chart purchases for this vessel.')
          lines.push('Upload the latest folio report or refresh to ensure coverage.')
          break
        case 'error':
          lines.push('We were unable to evaluate the folio status.')
          lines.push('Please try again later or contact support if the issue persists.')
          break
        default:
          lines.push('We are gathering chart information for this vessel.')
          lines.push('Refresh the folio data if you need immediate status updates.')
      }
      return lines
    },
    chartDetails() {
      if (!Array.isArray(this.payload?.chartDetails)) {
        return []
      }
      return this.payload.chartDetails
        .filter(item => item && item.id)
        .map(item => ({
          id: item.id,
          type: item.type || null,
          daysLeft: typeof item.daysLeft === 'number' ? Math.round(item.daysLeft) : item.daysLeft,
        }))
    },
    chartPreview() {
      return this.chartDetails.slice(0, 6)
    },
    extraChartsCount() {
      const hiddenCount = this.chartDetails.length - this.chartPreview.length
      return hiddenCount > 0 ? hiddenCount : 0
    },
    hasChartDetails() {
      return this.chartDetails.length > 0
    },
    chartsLabel() {
      if (this.status === 'expired_all') {
        return 'Expired charts'
      }
      if (this.status === 'expiring') {
        return 'Charts to review'
      }
      return 'Tracked charts'
    },
    showPrimary() {
      return ['expired_all', 'expiring'].includes(this.status)
    },
    primaryLabel() {
      if (this.status === 'expired_all') {
        return 'VIEW FOLIO'
      }
      if (this.status === 'expiring') {
        return 'VIEW FOLIO'
      }
      return 'OK'
    },
  },
  watch: {
    payload: {
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.$bvModal.show('alerts-folio-modal')
          })
        }
      },
      immediate: true,
    },
  },
  methods: {
    handlePrimary() {
      this.$emit('purchase')
    },
    handleClose() {
      this.$bvModal.hide('alerts-folio-modal')
      this.$emit('close')
    },
    formatDaysLeft(value) {
      if (typeof value !== 'number' || Number.isNaN(value)) {
        return 'Unknown'
      }
      const rounded = Math.trunc(value)
      if (rounded <= 0) {
        return 'Expired'
      }
      if (rounded === 1) {
        return '1 day left'
      }
      return `${rounded} days left`
    },
    getDaysClass(value) {
      if (typeof value !== 'number' || Number.isNaN(value)) {
        return 'days-unknown'
      }
      if (value <= 0) {
        return 'days-expired'
      }
      if (value <= 30) {
        return 'days-expiring'
      }
      return 'days-healthy'
    },
  },
}
</script>

<style scoped>
.folio-alert-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
}

.title-label {
  color: #1f2940;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.title-meta {
  color: #5b6480;
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.modal-body-content {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 4px 2px 8px;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
  color: #1f2940;
}

.message-block {
  background: linear-gradient(135deg, rgba(250, 251, 255, 0.95), rgba(234, 237, 255, 0.9));
  border: 1px solid rgba(209, 214, 233, 0.7);
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 14px 28px rgba(24, 32, 60, 0.12);
}

.message-line {
  margin: 0 0 10px;
  font-size: 0.87rem;
  font-weight: 500;
  line-height: 1.4;
  color: #1f2940;
}

.message-line:last-child {
  margin-bottom: 0;
}

.charts-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 18px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(226, 229, 240, 0.85);
  box-shadow: 0 12px 24px rgba(24, 32, 60, 0.12);
}

.charts-label {
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 600;
  color: #56618a;
}

.charts-list {
  margin: 0;
  padding-left: 0;
  font-size: 0.82rem;
  color: #1f2940;
  display: flex;
  flex-direction: column;
  gap: 6px;
  list-style: none;
}

.charts-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 1.3;
  padding: 6px 10px;
  border-radius: 10px;
  background: rgba(243, 245, 255, 0.6);
}

.chart-id {
  font-weight: 600;
}

.chart-days {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.days-healthy {
  background: rgba(64, 181, 142, 0.16);
  color: #2d8662;
}

.days-expiring {
  background: rgba(255, 193, 58, 0.22);
  color: #9f6805;
}

.days-expired {
  background: rgba(234, 69, 86, 0.18);
  color: #a32838;
}

.days-unknown {
  background: rgba(132, 140, 165, 0.18);
  color: #4f566d;
}

.charts-footnote {
  font-size: 0.75rem;
  color: #5b6480;
  margin-top: -4px;
}

.modal-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.action-button {
  min-width: 120px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 600;
}

.action-button.primary {
  background: linear-gradient(135deg, #4b5fdd, #5669f5);
  border-color: rgba(75, 95, 221, 0.2);
}

::v-deep .alerts-folio-modal__dialog {
  max-width: 560px;
}

::v-deep .alerts-folio-modal__content {
  border-radius: 18px;
  border: none;
  box-shadow: 0 24px 40px rgba(20, 30, 55, 0.18);
}

::v-deep .alerts-folio-modal__header {
  background: linear-gradient(135deg, #f4f6ff 0%, #e6e9ff 100%);
  border-bottom: none;
  padding: 18px 22px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  box-shadow: inset 0 -1px 0 rgba(98, 111, 149, 0.12);
}

::v-deep .alerts-folio-modal__body {
  padding: 20px 24px 24px;
}
</style>
